import { isEqual, merge, sortBy } from 'lodash';

import {
  AlgorithmStep,
  AlgorithmControlStructure
} from '../../types/algorithm';
import { TeamContextState } from '../../contexts/team';
import { GameState, GameStatus } from '../../types/state';
import { PlayerContextState } from '../../contexts/player';
import { GameDocumentState } from '../../contexts/game-document';
import { ElapsedTime, PausedStatus } from '../../types/state/game/game-state';

/**
 * Start game
 * @param gameState - The gameState
 */
export const StartGameAsync = async (gameState: GameState) => {
  if (!gameState.elapsedTime) {
    gameState.elapsedTime = [];
  }
  const started: string = new Date().toISOString();
  gameState.elapsedTime.push({
    startedTime: started,
    pausedTime: undefined
  });

  return merge(gameState, {
    status: 'Starting' as GameStatus,
    startedUtc: started,
    elapsedTime: gameState.elapsedTime
  });
};

/**
 * End game
 * @param gameState - The gameState
 */
export const EndGameAsync = async (gameState: GameState) => {
  const lastIndex: number = (gameState.elapsedTime?.length ?? 0) - 1;
  return merge(gameState, {
    status: 'Finished' as GameStatus,
    pausedStatus: undefined as PausedStatus,
    finishedUtc: new Date().toISOString(),
    elapsedTime: gameState.elapsedTime?.map<ElapsedTime>(
      (item: ElapsedTime, index: number) =>
        lastIndex === index
          ? { ...item, pausedTime: new Date().toISOString() }
          : { ...item }
    )
  });
};

/**
 * Pause game
 * @param gameState - The gameState
 * @param currentStatus - The current status before game paused
 */
export const PausedGameAsync = async (
  gameState: GameState,
  currentStatus: GameStatus
) => {
  const lastIndex: number = (gameState.elapsedTime?.length ?? 0) - 1;
  return merge(gameState, {
    status: 'Paused' as GameStatus,
    pausedStatus: currentStatus as PausedStatus,
    elapsedTime: gameState.elapsedTime?.map<ElapsedTime>(
      (item: ElapsedTime, index: number) =>
        lastIndex === index
          ? { ...item, pausedTime: new Date().toISOString() }
          : { ...item }
    )
  });
};

/**
 * Resume game
 * @param gameState - The gameState
 */
export const ResumeGameAsync = async (gameState: GameState) => {
  if (!gameState.elapsedTime) {
    gameState.elapsedTime = [];
  }
  const started: string = new Date().toISOString();
  gameState.elapsedTime.push({
    startedTime: started,
    pausedTime: undefined
  });
  return merge(gameState, {
    status: gameState?.pausedStatus as GameStatus,
    pausedStatus: undefined as PausedStatus,
    elapsedTime: gameState.elapsedTime
  });
};

export const executeControlStructureStepEventHandler = async (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  if (stepControlStructure.identifier === 'score') {
    if (stepControlStructure.condition === 'is') {
      if (stepControlStructure.operation === 'greaterOrEqual') {
        return controlStructureScoreIsGreaterOrEqualThanArgumentQuantity(
          stepControlStructure,
          player,
          team
        );
      }

      if (stepControlStructure.operation === 'less') {
        return controlStructureScoreIsLessThanArgumentQuantity(
          stepControlStructure,
          player,
          team
        );
      }
    }

    if (stepControlStructure.condition === 'isNot') {
      if (stepControlStructure.operation === 'greaterOrEqual') {
        return controlStructureScoreIsLessOrEqualThanArgumentQuantity(
          stepControlStructure,
          player,
          team
        );
      }

      if (stepControlStructure.operation === 'less') {
        return controlStructureScoreIsGreaterThanArgumentQuantity(
          stepControlStructure,
          player,
          team
        );
      }
    }
    return;
  }

  if (stepControlStructure.condition === 'does') {
    if (stepControlStructure.operation === 'haveItem') {
      return controlStructurePlayerDoesHaveItem(
        stepControlStructure,
        player,
        team
      );
    } else if (stepControlStructure.operation === 'haveRole') {
      return controlStructurePlayerDoesHaveRole(
        stepControlStructure,
        player,
        team
      );
    } else if (stepControlStructure.operation === 'haveScore') {
      return controlStructurePlayerDoesHaveScore(
        stepControlStructure,
        player,
        team
      );
    } else if (stepControlStructure.operation === 'choosesAnswer') {
      return controlStructurePlayerDoesChoosesAnswer(
        stepControlStructure,
        player,
        team,
        'does'
      );
    }
  } else if (stepControlStructure.condition === 'doesNot') {
    if (stepControlStructure.operation === 'haveItem') {
      return controlStructurePlayerDoesNotHaveItem(
        stepControlStructure,
        player,
        team
      );
    } else if (stepControlStructure.operation === 'haveRole') {
      return controlStructurePlayerDoesNotHaveRole(
        stepControlStructure,
        player,
        team
      );
    } else if (stepControlStructure.operation === 'haveScore') {
      return controlStructurePlayerDoesNotHaveScore(
        stepControlStructure,
        player,
        team
      );
    } else if (stepControlStructure.operation === 'choosesAnswer') {
      return controlStructurePlayerDoesChoosesAnswer(
        stepControlStructure,
        player,
        team,
        'doesNot'
      );
    }
  }
};

/**
 * Check compound statement has valid statement or not
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
export const isValidCompoundStatement = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  if (stepControlStructure.condition === 'does') {
    if (stepControlStructure.operation === 'haveItem') {
      let itemQuantity = checkItemQuantity(stepControlStructure, player, team);

      if (itemQuantity >= stepControlStructure?.argumentQuantity!) {
        return true;
      } else {
        return false;
      }
    } else if (stepControlStructure.operation === 'haveRole') {
      if (checkIsHavingRole(stepControlStructure, player, team)) {
        return true;
      } else {
        return false;
      }
    } else if (stepControlStructure.operation === 'haveScore') {
      const isOnCompleteTaskAction = stepControlStructure.action
        ?.toLowerCase()
        .includes('oncomplete');
      let score =
        isOnCompleteTaskAction && stepControlStructure.identifier === 'player'
          ? getTotalScoreOnLatestCompletedTask(player)
          : checkIsHavingScore(stepControlStructure, player, team);

      if (score >= stepControlStructure?.argumentQuantity!) {
        return true;
      } else {
        return false;
      }
    }
  } else if (stepControlStructure.condition === 'doesNot') {
    if (stepControlStructure.operation === 'haveItem') {
      let itemQuantity = checkItemQuantity(stepControlStructure, player, team);

      if (itemQuantity < stepControlStructure?.argumentQuantity!) {
        return true;
      } else {
        return false;
      }
    } else if (stepControlStructure.operation === 'haveRole') {
      if (!checkIsHavingRole(stepControlStructure, player, team)) {
        return true;
      } else {
        return false;
      }
    } else if (stepControlStructure.operation === 'haveScore') {
      const isOnCompleteTaskAction = stepControlStructure.action
        ?.toLowerCase()
        .includes('oncomplete');
      let score =
        isOnCompleteTaskAction && stepControlStructure.identifier === 'player'
          ? getTotalScoreOnLatestCompletedTask(player)
          : checkIsHavingScore(stepControlStructure, player, team);
      if (score < stepControlStructure?.argumentQuantity!) {
        return true;
      } else {
        return false;
      }
    }
  }
};

/**
 * Check all statement valid or not
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const isValidStatement = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState,
  parentResult: boolean
) => {
  const compoundStatements = stepControlStructure?.ifCompoundStatements;

  if (!compoundStatements || compoundStatements.length === 0) {
    return parentResult;
  }

  const andStatements = compoundStatements.filter((x) => x.type === 'and');
  const orStatements = compoundStatements.filter((x) => x.type === 'or');

  if (
    andStatements.every((andStatement) =>
      isValidCompoundStatement(
        andStatement as AlgorithmControlStructure,
        player,
        team
      )
    )
  ) {
    if (parentResult) {
      return true;
    }
  }

  if (
    orStatements.some((orStatement) =>
      isValidCompoundStatement(
        orStatement as AlgorithmControlStructure,
        player,
        team
      )
    )
  ) {
    if (parentResult) return true;

    return true;
  }

  return false;
};

/**
 * Get player or team item quantity
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const checkItemQuantity = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let itemQuantity =
    stepControlStructure?.identifier === 'player'
      ? (player?.playerState?.inventory?.find(
          (x) => x.id === stepControlStructure?.argumentAssetId
        )?.quantity ?? 0)
      : (team?.teamState?.inventory?.find(
          (x) => x.id === stepControlStructure?.argumentAssetId
        )?.quantity ?? 0);

  return itemQuantity;
};

/**
 * Check player or team have a role or not
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const checkIsHavingRole = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  return stepControlStructure?.identifier === 'player'
    ? player?.playerState?.titles?.find(
        (x) => x === stepControlStructure?.argumentAssetId
      )
    : team?.teamState?.titles?.find(
        (x) => x === stepControlStructure?.argumentAssetId
      );
};

/**
 * Get player or team score
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const checkIsHavingScore = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  return stepControlStructure?.identifier === 'player'
    ? (player?.playerState?.score ?? 0)
    : (team?.teamState?.score ?? 0);
};

/**
 * Get player total score on latest completed task
 * @param player - PlayerContextState
 * @returns total score (0 if no task or answers found)
 */
const getTotalScoreOnLatestCompletedTask = (
  player: PlayerContextState
): number => {
  let totalScore: number = 0;

  // Find latest completed task
  const latestCompletedTask = player.playerState!.tasks!.find(
    (item) => item?.id === player.playerState?.latestCompletedTaskId
  );

  // No task found
  if (!latestCompletedTask) {
    return totalScore;
  }

  // Validate if there are answers
  if (
    latestCompletedTask.taskContentFormAnswers &&
    latestCompletedTask.taskContentFormAnswers.length > 0
  ) {
    latestCompletedTask.taskContentFormAnswers.forEach((item) => {
      if (item?.score) {
        totalScore += item.score;
      }
    });
  }

  return totalScore;
};

/**
 * Check player or team have an item
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructurePlayerDoesHaveItem = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let itemQuantity = checkItemQuantity(stepControlStructure, player, team);

  let parentResult = itemQuantity >= stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check player or team have no item
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructurePlayerDoesNotHaveItem = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let itemQuantity = checkItemQuantity(stepControlStructure, player, team);

  let parentResult = itemQuantity < stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check player or team have a role
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructurePlayerDoesHaveRole = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let parentResult = checkIsHavingRole(stepControlStructure, player, team)
    ? true
    : false;
  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );
  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check player or team have no role
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructurePlayerDoesNotHaveRole = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let parentResult = !checkIsHavingRole(stepControlStructure, player, team)
    ? true
    : false;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    //Add validation compount statement
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check player or team have a score
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructurePlayerDoesHaveScore = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  const isOnCompleteTaskAction = stepControlStructure.action
    ?.toLowerCase()
    .includes('oncomplete');

  let score =
    isOnCompleteTaskAction && stepControlStructure.identifier === 'player'
      ? getTotalScoreOnLatestCompletedTask(player)
      : checkIsHavingScore(stepControlStructure, player, team);

  let parentResult = score >= stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

const controlStructurePlayerDoesChoosesAnswer = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState,
  condition: 'does' | 'doesNot'
) => {
  // first index is formId
  const formId = (stepControlStructure.argumentAssetId as string[]).at(0) ?? '';
  const answerCondition = (
    stepControlStructure.argumentAssetId as string[]
  ).slice(1);

  let answer: string[] = [];
  for (const task of player.playerState?.tasks!) {
    if (task.taskContentFormAnswers?.map((t) => t.formId).includes(formId)) {
      const formAnswer = task.taskContentFormAnswers.find(
        (t) => t.formId === formId
      );
      if (formAnswer) {
        answer = (formAnswer.answers || [])
          .filter((a) => a.value === true)
          .map((a) => a.answerResId);
        break;
      }
    }
  }

  const parentResult =
    condition === 'does'
      ? isEqual(sortBy(answerCondition), sortBy(answer))
      : !isEqual(sortBy(answerCondition), sortBy(answer));

  const isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check player or team have no score
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructurePlayerDoesNotHaveScore = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  const isOnCompleteTaskAction = stepControlStructure.action
    ?.toLowerCase()
    .includes('oncomplete');

  let score =
    isOnCompleteTaskAction && stepControlStructure.identifier === 'player'
      ? getTotalScoreOnLatestCompletedTask(player)
      : checkIsHavingScore(stepControlStructure, player, team);

  let parentResult = score < stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check task score is greater or equal than argument quantity
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructureScoreIsGreaterOrEqualThanArgumentQuantity = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let score = getTotalScoreOnLatestCompletedTask(player);

  let parentResult = score >= stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check task score is greater or equal than argument quantity
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructureScoreIsLessOrEqualThanArgumentQuantity = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let score = getTotalScoreOnLatestCompletedTask(player);

  let parentResult = score <= stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check task score is less than argument quantity
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructureScoreIsLessThanArgumentQuantity = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let score = getTotalScoreOnLatestCompletedTask(player);

  let parentResult = score < stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

/**
 * Check task score is less than argument quantity
 * @param stepControlStructure
 * @param player
 * @param team
 * @returns
 */
const controlStructureScoreIsGreaterThanArgumentQuantity = (
  stepControlStructure: AlgorithmControlStructure,
  player: PlayerContextState,
  team: TeamContextState
) => {
  let score = getTotalScoreOnLatestCompletedTask(player);

  let parentResult = score > stepControlStructure?.argumentQuantity!;

  let isValidCompoundStatement = isValidStatement(
    stepControlStructure,
    player,
    team,
    parentResult
  );

  if (isValidCompoundStatement) {
    return controlStructureIFStep(stepControlStructure);
  } else {
    return controlStructureELSEStep(stepControlStructure);
  }
};

const controlStructureIFStep = (
  stepControlStructure: AlgorithmControlStructure
) => {
  return stepControlStructure?.ifSteps ?? [];
};

const controlStructureELSEStep = (
  stepControlStructure: AlgorithmControlStructure
) => {
  return stepControlStructure?.elseSteps ?? [];
};

/**
 * Get steps of the event action onTaskCompleted base on location (map, zone)
 * @param location - world-map | zoneId
 * @param gameDocument - game document
 * @return steps - AlogithmSteps[] | AlgorithmControlStructure[]
 */

export const getStepTaskCompletedMap = (
  location: string,
  gameDocument: GameDocumentState
): AlgorithmStep[] | AlgorithmControlStructure[] => {
  if (location === 'world-map') {
    const worldMap = gameDocument?.gameDocument?.rules?.worldMap;
    if (worldMap) {
      const { events: { onTaskCompletedMap: { steps = [] } = {} } = {} } =
        worldMap;

      return steps || [];
    }
  } else {
    const zone = gameDocument?.gameDocument?.assets?.zones?.find(
      (x) => x.id === location
    );
    if (zone) {
      const { events: { onTaskCompletedMap: { steps = [] } = {} } = {} } = zone;
      return steps || [];
    }
  }
  return [];
};
